/**
 * HowToPlayCard — permanent, collapsible instructional card shown near the top of Home.
 * Always visible (even to repeat visitors) so no one can miss the rules.
 * Users can collapse it; state persists in localStorage.
 */
import React, { useState, useEffect } from 'react';
import { HelpCircle, ChevronDown, Wallet, Grid3x3, Target, Sparkles } from 'lucide-react';

const STORAGE_KEY = 'vcg_how_to_play_collapsed';

export const HowToPlayCard = ({ isLoggedIn, onOpenModal, onLogin, onDeposit }) => {
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    try { setCollapsed(localStorage.getItem(STORAGE_KEY) === '1'); } catch { /* noop */ }
  }, []);

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem(STORAGE_KEY, next ? '1' : '0'); } catch { /* noop */ }
  };

  return (
    <section data-testid="how-to-play-card" className="rounded-2xl border border-orange-500/25 bg-gradient-to-br from-orange-500/10 via-amber-500/5 to-transparent overflow-hidden">
      <button
        data-testid="how-to-play-toggle"
        onClick={toggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-orange-500/5 transition-colors"
      >
        <div className="flex items-center gap-2">
          <HelpCircle className="w-4 h-4 text-orange-400" />
          <span className="text-sm font-bold text-white">How to Play</span>
          <span className="text-[10px] text-emerald-300/70 bg-emerald-500/10 px-1.5 py-0.5 rounded-full font-semibold">
            Play more, pay less
          </span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-slate-400 transition-transform ${collapsed ? '' : 'rotate-180'}`}
        />
      </button>

      {!collapsed && (
        <div className="px-4 pb-4 space-y-3 text-xs">
          {/* Step 1 */}
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-7 h-7 rounded-full bg-orange-500/20 border border-orange-500/40 flex items-center justify-center">
              <Wallet className="w-3.5 h-3.5 text-orange-300" />
            </div>
            <div className="flex-1 pt-0.5">
              <p className="font-semibold text-white text-[13px]">1. Fund your account</p>
              <p className="text-slate-400 leading-snug">
                {isLoggedIn
                  ? 'Deposit XRP from your Xaman wallet in Settings, or connect Xaman for one-tap top-ups.'
                  : 'Tap Login in the top-right → sign up → deposit XRP or connect Xaman.'}
              </p>
              {!isLoggedIn && onLogin && (
                <button
                  data-testid="how-to-play-login-btn"
                  onClick={onLogin}
                  className="mt-1 text-[11px] text-orange-300 hover:text-orange-200 font-semibold underline decoration-dotted"
                >
                  Log in / Sign up →
                </button>
              )}
              {isLoggedIn && onDeposit && (
                <button
                  data-testid="how-to-play-deposit-btn"
                  onClick={onDeposit}
                  className="mt-1 text-[11px] text-orange-300 hover:text-orange-200 font-semibold underline decoration-dotted"
                >
                  Deposit XRP →
                </button>
              )}
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
              <Grid3x3 className="w-3.5 h-3.5 text-emerald-300" />
            </div>
            <div className="flex-1 pt-0.5">
              <p className="font-semibold text-white text-[13px]">2. Pick a room</p>
              <p className="text-slate-400 leading-snug">
                <span className="text-emerald-300">XRP Pool</span> rooms — winner takes the pot. House fee starts at 9.85% and drops to 3.21% as you play more.
                <br />
                <span className="text-purple-300">Prize</span> rooms — NFTs & physical items from hosts. Flat 5.89% platform fee.
              </p>
            </div>
          </div>

          {/* Step 3 */}
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
              <Target className="w-3.5 h-3.5 text-amber-300" />
            </div>
            <div className="flex-1 pt-0.5">
              <p className="font-semibold text-white text-[13px]">3. Pick slot 0–9</p>
              <p className="text-slate-400 leading-snug">
                Each room has 10 slots. Win if your slot matches the day's
                <span className="text-amber-300 font-semibold"> Illinois Pick 3 Fireball</span>{' '}
                — midday & evening draws, <span className="text-white">midnight CT cutoff</span> to buy.
              </p>
            </div>
          </div>

          {/* Step 4 */}
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-7 h-7 rounded-full bg-fuchsia-500/20 border border-fuchsia-500/40 flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-fuchsia-300" />
            </div>
            <div className="flex-1 pt-0.5">
              <p className="font-semibold text-white text-[13px]">4. Win → Sell or Ship</p>
              <p className="text-slate-400 leading-snug">
                Won a physical prize?{' '}
                <span className="text-orange-300">Sell</span> for 32.1% cash and let the host keep the item, or{' '}
                <span className="text-emerald-300">Ship</span> and take home the prize.
              </p>
            </div>
          </div>

          {onOpenModal && (
            <button
              data-testid="how-to-play-open-tour"
              onClick={onOpenModal}
              className="w-full text-center text-[11px] text-orange-300 hover:text-orange-200 font-semibold py-2 border-t border-orange-500/20 mt-2"
            >
              Open the full walkthrough →
            </button>
          )}
        </div>
      )}
    </section>
  );
};

export default HowToPlayCard;
